/**
 * `TemporaryBackofficeSession.type` and `.status` are plain `String` in the
 * GraphQL schema, so these are the values we know about.
 */
export const SessionStatus = {
  /** Session created, but no token generated for it yet */
  Opened: 'opened',
  /** A token has been generated, the session is in use */
  Active: 'active',
  /** Ran past its `expiresAt` without being closed */
  Expired: 'expired',
  /** Closed by the actor */
  Closed: 'closed',
} as const

export const SessionType = {
  Impersonation: 'impersonation',
  ScopedAccess: 'scopedAccess',
} as const

/**
 * Minutes the prolong mutation pushes a session's expiry out by. The service
 * decides the amount and does not report it, so this only exists to name it in
 * the UI.
 */
export const SESSION_PROLONG_BY_IN_MINUTES = 30

/**
 * Statuses of a session that has neither expired nor been closed, and whose
 * permissions can therefore still be used.
 */
export const ONGOING_SESSION_STATUSES: string[] = [
  SessionStatus.Opened,
  SessionStatus.Active,
]

/**
 * Whether a session impersonates a specific user, as opposed to granting scoped
 * access to an organization. The two are presented differently everywhere: an
 * impersonation session names the impersonated user, a scoped-access one the
 * organization.
 */
export const isImpersonationSession = (session: { type: string }): boolean =>
  session.type === SessionType.Impersonation

/**
 * Display name of the organization with `organizationId`, falling back to the
 * raw id when it cannot be resolved.
 */
export const getOrganizationName = (
  organizations: readonly { id: string; name: string }[] | undefined,
  organizationId: string,
): string =>
  organizations?.find(organization => organization.id === organizationId)
    ?.name ?? organizationId
